/**
 * useSiteAnalytics - Hook for fetching and aggregating site analytics data
 *
 * Uses pre-aggregated daily data for historical queries (fast, scalable)
 * Falls back to raw events for today's data (real-time)
 *
 * Provides:
 * - Overview metrics (visitors, pageviews, bounce rate, etc.)
 * - Time series data for charts
 * - Breakdowns by source, page, device, browser, country
 * - UTM campaign breakdowns
 * - Period-over-period trend comparisons
 */

import { useState, useEffect, useCallback } from 'react'
import { supabase } from '@/lib/supabase'

// Types
export interface AnalyticsOverview {
  visitors: number
  pageviews: number
  sessions: number
  bounceRate: number
  avgSessionDuration: number // in seconds
  newVisitorRate: number
  conversions?: number
  conversionRate?: number // as percentage of sessions
}

export interface TimeSeriesDataPoint {
  date: string
  visitors: number
  pageviews: number
  sessions: number
}

export interface SourceBreakdown {
  source: string
  count: number
  percentage: number
}

export interface PageBreakdown {
  path: string
  title: string | null
  views: number
  percentage: number
}

export interface DeviceBreakdown {
  device: string
  count: number
  percentage: number
}

export interface BrowserBreakdown {
  browser: string
  count: number
  percentage: number
}

export interface CountryBreakdown {
  country: string
  countryCode: string | null
  count: number
  percentage: number
}

export interface UTMBreakdown {
  value: string
  count: number
  percentage: number
}

export interface ChannelBreakdown {
  channel: string
  count: number
  percentage: number
}

export interface LandingExitPage {
  path: string
  title: string | null
  sessions: number
}

export interface OutboundClick {
  domain: string
  clicks: number
}

export interface SearchTerm {
  term: string
  count: number
}

export interface FormDetail {
  form_id: string
  page_path: string
  count: number
}

export interface ConversionBreakdown {
  event_name: string
  count: number
  conversion_rate: number // as percentage of sessions
}

export interface ChannelConversion {
  channel: string
  conversions: number
}

export interface TrendData {
  visitors: { delta: number | null; direction: 'up' | 'down' | 'neutral' }
  pageviews: { delta: number | null; direction: 'up' | 'down' | 'neutral' }
  bounceRate: { delta: number | null; direction: 'up' | 'down' | 'neutral' }
  avgSessionDuration: { delta: number | null; direction: 'up' | 'down' | 'neutral' }
}

export interface SiteAnalyticsData {
  overview: AnalyticsOverview & { avgScrollDepth?: number; formSubmissions?: number }
  timeSeries: TimeSeriesDataPoint[]
  previousTimeSeries: TimeSeriesDataPoint[] // Previous period for comparison overlay
  sources: SourceBreakdown[]
  channels: ChannelBreakdown[]
  topPages: PageBreakdown[]
  devices: DeviceBreakdown[]
  browsers: BrowserBreakdown[]
  countries: CountryBreakdown[]
  utmSources: UTMBreakdown[]
  utmMediums: UTMBreakdown[]
  utmCampaigns: UTMBreakdown[]
  landingPages: LandingExitPage[]
  exitPages: LandingExitPage[]
  outboundClicks: OutboundClick[]
  searchTerms: SearchTerm[]
  formDetails: FormDetail[]
  conversionBreakdown: ConversionBreakdown[]
  channelConversions: ChannelConversion[]
  trends: TrendData | null
}

export type DateRange = '7d' | '30d' | '90d' | '1y' | 'custom'

interface UseSiteAnalyticsOptions {
  projectId: string
  dateRange: DateRange
  customStartDate?: Date
  customEndDate?: Date
}

interface UseSiteAnalyticsResult {
  data: SiteAnalyticsData | null
  loading: boolean
  error: string | null
  hasGa4Data: boolean
  hasHistoricalData: boolean  // true when monthly summary data is included
  hasAnyData: boolean  // true when at least one real row came from the database (not gap-filled)
  refresh: () => Promise<void>
}

// Aggregated daily row type from database
interface DailyAggRow {
  date: string
  data_source?: string // 'framed' | 'ga4'
  pageviews: number
  unique_visitors: number
  sessions: number
  new_visitors: number
  bounced_sessions: number
  total_session_duration_seconds: number
  sessions_with_duration: number
  sources: { source: string; count: number }[]
  channels?: { channel: string; count: number }[]
  top_pages: { path: string; title: string | null; views: number }[]
  devices: Record<string, number>
  browsers: Record<string, number>
  countries: Record<string, { count: number; code: string | null }>
  utm_sources?: { source: string; count: number }[]
  utm_mediums?: { medium: string; count: number }[]
  utm_campaigns?: { campaign: string; count: number }[]
  avg_scroll_depth?: number
  landing_pages?: { path: string; title: string | null; sessions: number }[]
  exit_pages?: { path: string; title: string | null; sessions: number }[]
  outbound_clicks?: { domain: string; clicks: number }[]
  form_submissions?: number
  form_details?: { form_id: string; page_path: string; count: number }[]
  search_terms?: { term: string; count: number }[]
  conversions?: number
  conversion_rate?: number
}

/**
 * Deduplicate rows when both framed and ga4 data exist for the same date.
 * Framed data takes precedence over ga4 data.
 */
function deduplicateBySource(rows: DailyAggRow[]): DailyAggRow[] {
  const dateMap = new Map<string, DailyAggRow>()
  for (const row of rows) {
    const existing = dateMap.get(row.date)
    if (!existing) {
      dateMap.set(row.date, row)
    } else {
      // Framed data takes precedence over ga4
      const existingSource = existing.data_source || 'framed'
      const newSource = row.data_source || 'framed'
      if (newSource === 'framed' && existingSource !== 'framed') {
        dateMap.set(row.date, row)
      }
    }
  }
  return Array.from(dateMap.values()).sort((a, b) => a.date.localeCompare(b.date))
}

// Helper to calculate date range
function getDateRangeFilter(dateRange: DateRange, customStart?: Date, customEnd?: Date): { start: Date; end: Date } {
  const end = new Date()
  end.setHours(23, 59, 59, 999)

  let start: Date

  switch (dateRange) {
    case '7d':
      start = new Date()
      start.setDate(start.getDate() - 7)
      break
    case '30d':
      start = new Date()
      start.setDate(start.getDate() - 30)
      break
    case '90d':
      start = new Date()
      start.setDate(start.getDate() - 90)
      break
    case '1y':
      start = new Date()
      start.setFullYear(start.getFullYear() - 1)
      break
    case 'custom':
      start = customStart || new Date()
      if (customEnd) {
        end.setTime(customEnd.getTime())
        end.setHours(23, 59, 59, 999)
      }
      break
    default:
      start = new Date()
      start.setDate(start.getDate() - 7)
  }

  start.setHours(0, 0, 0, 0)
  return { start, end }
}

// Get the previous period range (same duration, immediately before current range)
function getPreviousPeriodRange(start: Date, end: Date): { start: Date; end: Date } {
  const durationMs = end.getTime() - start.getTime()
  const prevEnd = new Date(start.getTime() - 1)
  prevEnd.setHours(23, 59, 59, 999)
  const prevStart = new Date(prevEnd.getTime() - durationMs)
  prevStart.setHours(0, 0, 0, 0)
  return { start: prevStart, end: prevEnd }
}

// Format a Date as YYYY-MM-DD in local timezone (avoids UTC shift from toISOString)
function toDateStr(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

// Get today's date in YYYY-MM-DD format
function getTodayString(): string {
  return toDateStr(new Date())
}

// Generate a complete date series between start and end (inclusive), filling gaps with zero-value rows
function fillDateGaps(rows: DailyAggRow[], startDate: string, endDate: string): DailyAggRow[] {
  const rowMap = new Map<string, DailyAggRow>()
  for (const row of rows) {
    rowMap.set(row.date, row)
  }

  const result: DailyAggRow[] = []
  const current = new Date(startDate + 'T00:00:00')
  const end = new Date(endDate + 'T00:00:00')

  while (current <= end) {
    const dateStr = toDateStr(current)
    result.push(rowMap.get(dateStr) || {
      date: dateStr,
      pageviews: 0,
      unique_visitors: 0,
      sessions: 0,
      new_visitors: 0,
      bounced_sessions: 0,
      total_session_duration_seconds: 0,
      sessions_with_duration: 0,
      sources: [],
      channels: [],
      top_pages: [],
      devices: {},
      browsers: {},
      countries: {},
      utm_sources: [],
      utm_mediums: [],
      utm_campaigns: [],
    })
    current.setDate(current.getDate() + 1)
  }

  return result
}

// Calculate trend from current and previous values
function calculateTrend(current: number, previous: number, invertBetter = false): { delta: number | null; direction: 'up' | 'down' | 'neutral' } {
  if (previous === 0 && current === 0) return { delta: null, direction: 'neutral' }
  if (previous === 0) return { delta: 100, direction: invertBetter ? 'down' : 'up' }

  const delta = ((current - previous) / previous) * 100

  if (Math.abs(delta) < 1) return { delta: 0, direction: 'neutral' }

  const isPositive = delta > 0
  const direction = invertBetter
    ? (isPositive ? 'down' : 'up')
    : (isPositive ? 'up' : 'down')

  return { delta, direction }
}

// Aggregate rows into overview metrics
function aggregateOverview(rows: DailyAggRow[]): AnalyticsOverview {
  const totalPageviews = rows.reduce((sum, r) => sum + (r.pageviews || 0), 0)
  const totalVisitors = rows.reduce((sum, r) => sum + (r.unique_visitors || 0), 0)
  const totalSessions = rows.reduce((sum, r) => sum + (r.sessions || 0), 0)
  const totalNewVisitors = rows.reduce((sum, r) => sum + (r.new_visitors || 0), 0)
  const totalBouncedSessions = rows.reduce((sum, r) => sum + (r.bounced_sessions || 0), 0)
  const totalDuration = rows.reduce((sum, r) => sum + (r.total_session_duration_seconds || 0), 0)
  const totalSessionsWithDuration = rows.reduce((sum, r) => sum + (r.sessions_with_duration || 0), 0)

  const bounceRate = totalSessions > 0 ? (totalBouncedSessions / totalSessions) * 100 : 0
  const avgSessionDuration = totalSessionsWithDuration > 0 ? totalDuration / totalSessionsWithDuration : 0
  const newVisitorRate = totalPageviews > 0 ? (totalNewVisitors / totalPageviews) * 100 : 0

  return {
    visitors: totalVisitors,
    pageviews: totalPageviews,
    sessions: totalSessions,
    bounceRate: Math.round(bounceRate * 10) / 10,
    avgSessionDuration: Math.round(avgSessionDuration),
    newVisitorRate: Math.round(newVisitorRate)
  }
}

export function useSiteAnalytics({
  projectId,
  dateRange,
  customStartDate,
  customEndDate
}: UseSiteAnalyticsOptions): UseSiteAnalyticsResult {
  const [data, setData] = useState<SiteAnalyticsData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [hasGa4Data, setHasGa4Data] = useState(false)
  const [hasHistoricalData, setHasHistoricalData] = useState(false)
  const [hasAnyData, setHasAnyData] = useState(false)

  const fetchAnalytics = useCallback(async () => {
    if (!projectId) {
      setError('Project ID is required')
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)

    try {
      const { start, end } = getDateRangeFilter(dateRange, customStartDate, customEndDate)
      const startDateStr = toDateStr(start)
      const endDateStr = toDateStr(end)
      const todayStr = getTodayString()

      // Determine if date range extends beyond 90 days (into monthly-summary territory)
      const ninetyDaysAgo = new Date()
      ninetyDaysAgo.setDate(ninetyDaysAgo.getDate() - 90)
      ninetyDaysAgo.setHours(0, 0, 0, 0)
      const rangeExceedsRawWindow = start < ninetyDaysAgo

      // Fetch pre-aggregated data for current period
      const { data: aggData, error: aggError } = await supabase
        .from('site_analytics_daily_agg')
        .select('*')
        .eq('project_id', projectId)
        .gte('date', startDateStr)
        .lte('date', endDateStr)
        .order('date', { ascending: true })

      if (aggError) throw aggError

      // Deduplicate: when both framed and ga4 rows exist for same date, prefer framed
      const aggRows = deduplicateBySource((aggData || []) as DailyAggRow[])

      // Track whether the DB returned any real rows (vs gap-filled zeros)
      setHasAnyData(aggRows.length > 0)

      // For date ranges beyond 90 days, fetch monthly summaries and convert to daily-like rows
      let monthlyRows: DailyAggRow[] = []
      if (rangeExceedsRawWindow) {
        const historicalEndDate = new Date(ninetyDaysAgo.getTime() - 1)
        const historicalStartStr = startDateStr
        const historicalEndStr = toDateStr(historicalEndDate)

        const { data: monthlyData } = await supabase
          .from('site_analytics_monthly_summary')
          .select('*')
          .eq('project_id', projectId)
          .gte('month', historicalStartStr)
          .lte('month', historicalEndStr)
          .order('month', { ascending: true })

        if (monthlyData && monthlyData.length > 0) {
          // Convert monthly summary rows to DailyAggRow format (using month date as the date key)
          monthlyRows = monthlyData.map(m => ({
            date: m.month,
            pageviews: m.page_views || 0,
            unique_visitors: m.unique_visitors || 0,
            sessions: m.sessions || 0,
            new_visitors: m.new_visitors || 0,
            bounced_sessions: m.bounced_sessions || 0,
            total_session_duration_seconds: m.total_session_duration_seconds || 0,
            sessions_with_duration: m.sessions_with_duration || 0,
            top_pages: m.top_pages || [],
            sources: m.top_referrers || [],
            channels: m.channel_breakdown || [],
            devices: m.device_breakdown || {},
            countries: m.country_breakdown || {},
            utm_sources: m.utm_sources || [],
            utm_mediums: m.utm_mediums || [],
            utm_campaigns: m.utm_campaigns || [],
            browsers: {},
          }))
        }
      }
      setHasHistoricalData(monthlyRows.length > 0)
      if (monthlyRows.length > 0) setHasAnyData(true)

      // Track whether any GA4 data is present (for UI indicator)
      const hasGa4Data = (aggData || []).some((r: DailyAggRow) => r.data_source === 'ga4')

      // Check if we need today's real-time data
      let todayData: DailyAggRow | null = null
      const hasTodayInRange = endDateStr >= todayStr && startDateStr <= todayStr
      const hasTodayAgg = aggRows.some(r => r.date === todayStr)

      if (hasTodayInRange && !hasTodayAgg) {
        todayData = await fetchTodayRawData(projectId, todayStr)
        if (todayData && todayData.pageviews > 0) setHasAnyData(true)
      }

      // Combine monthly summaries + daily aggs + today's real-time data
      const rawRows = [
        ...monthlyRows,
        ...(todayData
          ? [...aggRows.filter(r => r.date !== todayStr), todayData]
          : aggRows)
      ].sort((a, b) => a.date.localeCompare(b.date))

      // Fill date gaps with zero-value rows so charts show continuous timeline
      const allRows = rangeExceedsRawWindow
        ? rawRows  // Don't gap-fill monthly data (too many empty days)
        : fillDateGaps(rawRows, startDateStr, endDateStr)

      setHasGa4Data(hasGa4Data)

      if (allRows.length === 0) {
        setData({
          overview: {
            visitors: 0,
            pageviews: 0,
            sessions: 0,
            bounceRate: 0,
            avgSessionDuration: 0,
            newVisitorRate: 0,
            avgScrollDepth: 0,
            formSubmissions: 0
          },
          timeSeries: [],
          previousTimeSeries: [],
          sources: [],
          channels: [],
          topPages: [],
          devices: [],
          browsers: [],
          countries: [],
          utmSources: [],
          utmMediums: [],
          utmCampaigns: [],
          landingPages: [],
          exitPages: [],
          outboundClicks: [],
          searchTerms: [],
          formDetails: [],
          conversionBreakdown: [],
          channelConversions: [],
          trends: null
        })
        setLoading(false)
        return
      }

      // Get current period overview
      const overview = aggregateOverview(allRows)

      // Fetch previous period for trend comparison
      const prevRange = getPreviousPeriodRange(start, end)
      const prevStartStr = toDateStr(prevRange.start)
      const prevEndStr = toDateStr(prevRange.end)

      const { data: prevAggData } = await supabase
        .from('site_analytics_daily_agg')
        .select('*')
        .eq('project_id', projectId)
        .gte('date', prevStartStr)
        .lte('date', prevEndStr)

      let prevRows = deduplicateBySource((prevAggData || []) as DailyAggRow[])

      // For long ranges, supplement previous period with monthly summaries
      const prevExceedsRawWindow = prevRange.start < ninetyDaysAgo
      if (prevExceedsRawWindow) {
        const prevHistEnd = new Date(Math.min(ninetyDaysAgo.getTime() - 1, prevRange.end.getTime()))
        const { data: prevMonthly } = await supabase
          .from('site_analytics_monthly_summary')
          .select('*')
          .eq('project_id', projectId)
          .gte('month', prevStartStr)
          .lte('month', toDateStr(prevHistEnd))
          .order('month', { ascending: true })

        if (prevMonthly && prevMonthly.length > 0) {
          const prevMonthlyRows: DailyAggRow[] = prevMonthly.map(m => ({
            date: m.month,
            pageviews: m.page_views || 0,
            unique_visitors: m.unique_visitors || 0,
            sessions: m.sessions || 0,
            new_visitors: m.new_visitors || 0,
            bounced_sessions: m.bounced_sessions || 0,
            total_session_duration_seconds: m.total_session_duration_seconds || 0,
            sessions_with_duration: m.sessions_with_duration || 0,
            top_pages: m.top_pages || [],
            sources: m.top_referrers || [],
            channels: m.channel_breakdown || [],
            devices: m.device_breakdown || {},
            countries: m.country_breakdown || {},
            utm_sources: m.utm_sources || [],
            utm_mediums: m.utm_mediums || [],
            utm_campaigns: m.utm_campaigns || [],
            browsers: {},
          }))
          prevRows = [...prevMonthlyRows, ...prevRows].sort((a, b) => a.date.localeCompare(b.date))
        }
      }
      let trends: TrendData | null = null

      if (prevRows.length > 0) {
        const prevOverview = aggregateOverview(prevRows)
        trends = {
          visitors: calculateTrend(overview.visitors, prevOverview.visitors),
          pageviews: calculateTrend(overview.pageviews, prevOverview.pageviews),
          bounceRate: calculateTrend(overview.bounceRate, prevOverview.bounceRate, true),
          avgSessionDuration: calculateTrend(overview.avgSessionDuration, prevOverview.avgSessionDuration)
        }
      }

      // Build previous period time series for comparison overlay
      const previousTimeSeries: TimeSeriesDataPoint[] = prevRows
        .map(r => ({
          date: r.date,
          visitors: r.unique_visitors || 0,
          pageviews: r.pageviews || 0,
          sessions: r.sessions || 0
        }))
        .sort((a, b) => a.date.localeCompare(b.date))

      const totalPageviews = overview.pageviews

      // Build time series
      const timeSeries: TimeSeriesDataPoint[] = allRows.map(r => ({
        date: r.date,
        visitors: r.unique_visitors || 0,
        pageviews: r.pageviews || 0,
        sessions: r.sessions || 0
      }))

      // Merge sources across days
      const sourceMap = new Map<string, number>()
      allRows.forEach(r => {
        const sources = r.sources || []
        sources.forEach(s => {
          sourceMap.set(s.source, (sourceMap.get(s.source) || 0) + s.count)
        })
      })
      const sources: SourceBreakdown[] = Array.from(sourceMap.entries())
        .map(([source, count]) => ({
          source,
          count,
          percentage: totalPageviews > 0 ? (count / totalPageviews) * 100 : 0
        }))
        .sort((a, b) => b.count - a.count)

      // Merge top pages across days
      const pageMap = new Map<string, { title: string | null; views: number }>()
      allRows.forEach(r => {
        const pages = r.top_pages || []
        pages.forEach(p => {
          const existing = pageMap.get(p.path)
          if (existing) {
            existing.views += p.views
            if (p.title && !existing.title) existing.title = p.title
          } else {
            pageMap.set(p.path, { title: p.title, views: p.views })
          }
        })
      })
      const topPages: PageBreakdown[] = Array.from(pageMap.entries())
        .map(([path, { title, views }]) => ({
          path,
          title,
          views,
          percentage: totalPageviews > 0 ? (views / totalPageviews) * 100 : 0
        }))
        .sort((a, b) => b.views - a.views)
        .slice(0, 10)

      // Merge devices across days
      const deviceMap = new Map<string, number>()
      allRows.forEach(r => {
        const devices = r.devices || {}
        Object.entries(devices).forEach(([device, count]) => {
          deviceMap.set(device, (deviceMap.get(device) || 0) + (count as number))
        })
      })
      const devices: DeviceBreakdown[] = Array.from(deviceMap.entries())
        .map(([device, count]) => ({
          device,
          count,
          percentage: totalPageviews > 0 ? (count / totalPageviews) * 100 : 0
        }))
        .sort((a, b) => b.count - a.count)

      // Merge browsers across days
      const browserMap = new Map<string, number>()
      allRows.forEach(r => {
        const browsers = r.browsers || {}
        Object.entries(browsers).forEach(([browser, count]) => {
          browserMap.set(browser, (browserMap.get(browser) || 0) + (count as number))
        })
      })
      const browsers: BrowserBreakdown[] = Array.from(browserMap.entries())
        .map(([browser, count]) => ({
          browser,
          count,
          percentage: totalPageviews > 0 ? (count / totalPageviews) * 100 : 0
        }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 10)

      // Merge countries across days
      const countryMap = new Map<string, { count: number; code: string | null }>()
      allRows.forEach(r => {
        const countries = r.countries || {}
        Object.entries(countries).forEach(([country, data]) => {
          const countryData = data as { count: number; code: string | null }
          const existing = countryMap.get(country)
          if (existing) {
            existing.count += countryData.count
            if (countryData.code && !existing.code) existing.code = countryData.code
          } else {
            countryMap.set(country, { count: countryData.count, code: countryData.code })
          }
        })
      })
      const countries: CountryBreakdown[] = Array.from(countryMap.entries())
        .map(([country, { count, code }]) => ({
          country,
          countryCode: code,
          count,
          percentage: totalPageviews > 0 ? (count / totalPageviews) * 100 : 0
        }))
        .sort((a, b) => b.count - a.count)

      // Merge UTM sources across days
      const utmSourceMap = new Map<string, number>()
      allRows.forEach(r => {
        const utmSources = r.utm_sources || []
        utmSources.forEach(s => {
          utmSourceMap.set(s.source, (utmSourceMap.get(s.source) || 0) + s.count)
        })
      })
      const totalUTMSourceCount = Array.from(utmSourceMap.values()).reduce((sum, c) => sum + c, 0)
      const utmSources: UTMBreakdown[] = Array.from(utmSourceMap.entries())
        .map(([value, count]) => ({
          value,
          count,
          percentage: totalUTMSourceCount > 0 ? (count / totalUTMSourceCount) * 100 : 0
        }))
        .sort((a, b) => b.count - a.count)

      // Merge UTM mediums across days
      const utmMediumMap = new Map<string, number>()
      allRows.forEach(r => {
        const utmMediums = r.utm_mediums || []
        utmMediums.forEach(m => {
          utmMediumMap.set(m.medium, (utmMediumMap.get(m.medium) || 0) + m.count)
        })
      })
      const totalUTMMediumCount = Array.from(utmMediumMap.values()).reduce((sum, c) => sum + c, 0)
      const utmMediums: UTMBreakdown[] = Array.from(utmMediumMap.entries())
        .map(([value, count]) => ({
          value,
          count,
          percentage: totalUTMMediumCount > 0 ? (count / totalUTMMediumCount) * 100 : 0
        }))
        .sort((a, b) => b.count - a.count)

      // Merge UTM campaigns across days
      const utmCampaignMap = new Map<string, number>()
      allRows.forEach(r => {
        const utmCampaigns = r.utm_campaigns || []
        utmCampaigns.forEach(c => {
          utmCampaignMap.set(c.campaign, (utmCampaignMap.get(c.campaign) || 0) + c.count)
        })
      })
      const totalUTMCampaignCount = Array.from(utmCampaignMap.values()).reduce((sum, c) => sum + c, 0)
      const utmCampaigns: UTMBreakdown[] = Array.from(utmCampaignMap.entries())
        .map(([value, count]) => ({
          value,
          count,
          percentage: totalUTMCampaignCount > 0 ? (count / totalUTMCampaignCount) * 100 : 0
        }))
        .sort((a, b) => b.count - a.count)

      // Merge channels across days
      const channelMap = new Map<string, number>()
      allRows.forEach(r => {
        const channels = r.channels || []
        channels.forEach(ch => {
          channelMap.set(ch.channel, (channelMap.get(ch.channel) || 0) + ch.count)
        })
      })
      const totalChannelCount = Array.from(channelMap.values()).reduce((sum, c) => sum + c, 0)
      const channels: ChannelBreakdown[] = Array.from(channelMap.entries())
        .map(([channel, count]) => ({
          channel,
          count,
          percentage: totalChannelCount > 0 ? (count / totalChannelCount) * 100 : 0
        }))
        .sort((a, b) => b.count - a.count)

      // Average scroll depth across days
      const scrollDepths = allRows.filter(r => r.avg_scroll_depth != null && r.avg_scroll_depth > 0)
      const avgScrollDepth = scrollDepths.length > 0
        ? Math.round(scrollDepths.reduce((sum, r) => sum + (r.avg_scroll_depth || 0), 0) / scrollDepths.length)
        : 0

      // Merge landing pages across days
      const landingPageMap = new Map<string, { title: string | null; sessions: number }>()
      allRows.forEach(r => {
        (r.landing_pages || []).forEach(lp => {
          const existing = landingPageMap.get(lp.path)
          if (existing) {
            existing.sessions += lp.sessions
            if (lp.title && !existing.title) existing.title = lp.title
          } else {
            landingPageMap.set(lp.path, { title: lp.title, sessions: lp.sessions })
          }
        })
      })
      const landingPages: LandingExitPage[] = Array.from(landingPageMap.entries())
        .map(([path, { title, sessions }]) => ({ path, title, sessions }))
        .sort((a, b) => b.sessions - a.sessions)
        .slice(0, 10)

      // Merge exit pages across days
      const exitPageMap = new Map<string, { title: string | null; sessions: number }>()
      allRows.forEach(r => {
        (r.exit_pages || []).forEach(ep => {
          const existing = exitPageMap.get(ep.path)
          if (existing) {
            existing.sessions += ep.sessions
            if (ep.title && !existing.title) existing.title = ep.title
          } else {
            exitPageMap.set(ep.path, { title: ep.title, sessions: ep.sessions })
          }
        })
      })
      const exitPages: LandingExitPage[] = Array.from(exitPageMap.entries())
        .map(([path, { title, sessions }]) => ({ path, title, sessions }))
        .sort((a, b) => b.sessions - a.sessions)
        .slice(0, 10)

      // Merge outbound clicks across days
      const outboundMap = new Map<string, number>()
      allRows.forEach(r => {
        (r.outbound_clicks || []).forEach(oc => {
          outboundMap.set(oc.domain, (outboundMap.get(oc.domain) || 0) + oc.clicks)
        })
      })
      const outboundClicks: OutboundClick[] = Array.from(outboundMap.entries())
        .map(([domain, clicks]) => ({ domain, clicks }))
        .sort((a, b) => b.clicks - a.clicks)
        .slice(0, 10)

      // Aggregate form submissions across days
      const totalFormSubmissions = allRows.reduce((sum, r) => sum + (r.form_submissions || 0), 0)

      const formDetailMap = new Map<string, { form_id: string; page_path: string; count: number }>()
      allRows.forEach(r => {
        (r.form_details || []).forEach(fd => {
          const key = `${fd.form_id}::${fd.page_path}`
          const existing = formDetailMap.get(key)
          if (existing) {
            existing.count += fd.count
          } else {
            formDetailMap.set(key, { ...fd })
          }
        })
      })
      const formDetails: FormDetail[] = Array.from(formDetailMap.values())
        .sort((a, b) => b.count - a.count)
        .slice(0, 10)

      // Aggregate search terms across days
      const searchTermMap = new Map<string, number>()
      allRows.forEach(r => {
        (r.search_terms || []).forEach(st => {
          const decoded = decodeSearchTerm(st.term)
          if (decoded) {
            searchTermMap.set(decoded, (searchTermMap.get(decoded) || 0) + st.count)
          }
        })
      })
      const searchTerms: SearchTerm[] = Array.from(searchTermMap.entries())
        .map(([term, count]) => ({ term, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 20)

      // Aggregate conversions from daily agg rows
      const totalConversions = allRows.reduce((sum, r) => sum + (r.conversions || 0), 0)
      const conversionRate = overview.sessions > 0
        ? Math.round((totalConversions / overview.sessions) * 1000) / 10
        : 0

      // Fetch conversion breakdown by event name from raw site_analytics
      // Note: conversion_event_name and is_conversion columns may not exist yet
      let conversionBreakdown: ConversionBreakdown[] = []
      let channelConversions: ChannelConversion[] = []
      try {
        const { data: convBreakdownData } = await supabase
          .from('site_analytics')
          .select('conversion_event_name')
          .eq('project_id', projectId)
          .eq('is_conversion', true)
          .gte('created_at', `${startDateStr}T00:00:00`)
          .lte('created_at', `${endDateStr}T23:59:59`)

        const convEventMap = new Map<string, number>()
        ;(convBreakdownData || []).forEach((row: { conversion_event_name: string | null }) => {
          const name = row.conversion_event_name || 'unknown'
          convEventMap.set(name, (convEventMap.get(name) || 0) + 1)
        })
        conversionBreakdown = Array.from(convEventMap.entries())
          .map(([event_name, count]) => ({
            event_name,
            count,
            conversion_rate: overview.sessions > 0 ? Math.round((count / overview.sessions) * 1000) / 10 : 0
          }))
          .sort((a, b) => b.count - a.count)

        // Fetch channel-level conversions
        const { data: convChannelData } = await supabase
          .from('site_analytics')
          .select('channel')
          .eq('project_id', projectId)
          .eq('is_conversion', true)
          .gte('created_at', `${startDateStr}T00:00:00`)
          .lte('created_at', `${endDateStr}T23:59:59`)

        const convChannelMap = new Map<string, number>()
        ;(convChannelData || []).forEach((row: { channel: string | null }) => {
          const ch = row.channel || 'direct'
          convChannelMap.set(ch, (convChannelMap.get(ch) || 0) + 1)
        })
        channelConversions = Array.from(convChannelMap.entries())
          .map(([channel, conversions]) => ({ channel, conversions }))
          .sort((a, b) => b.conversions - a.conversions)
      } catch {
        // Conversion columns not yet available — gracefully degrade
      }

      setData({
        overview: { ...overview, avgScrollDepth, formSubmissions: totalFormSubmissions, conversions: totalConversions, conversionRate },
        timeSeries,
        previousTimeSeries,
        sources,
        channels,
        topPages,
        devices,
        browsers,
        countries,
        utmSources,
        utmMediums,
        utmCampaigns,
        landingPages,
        exitPages,
        outboundClicks,
        searchTerms,
        formDetails,
        conversionBreakdown,
        channelConversions,
        trends
      })
    } catch (err) {
      console.error('Error fetching analytics:', err)
      setError(err instanceof Error ? err.message : 'Failed to fetch analytics')
    } finally {
      setLoading(false)
    }
  }, [projectId, dateRange, customStartDate, customEndDate])

  // Fetch on mount and when dependencies change
  useEffect(() => {
    fetchAnalytics()
  }, [fetchAnalytics])

  return {
    data,
    loading,
    error,
    hasGa4Data,
    hasHistoricalData,
    hasAnyData,
    refresh: fetchAnalytics
  }
}

/**
 * Fetch and aggregate today's raw data (for real-time display)
 * Only processes a single day of data so memory usage is minimal
 */
async function fetchTodayRawData(projectId: string, todayStr: string): Promise<DailyAggRow | null> {
  const startOfDay = new Date(todayStr)
  startOfDay.setHours(0, 0, 0, 0)
  const endOfDay = new Date(todayStr)
  endOfDay.setHours(23, 59, 59, 999)

  const { data: rawData, error } = await supabase
    .from('site_analytics')
    .select('*')
    .eq('project_id', projectId)
    .eq('event_type', 'pageview')
    .gte('created_at', startOfDay.toISOString())
    .lte('created_at', endOfDay.toISOString())

  if (error || !rawData || rawData.length === 0) {
    return null
  }

  // Calculate metrics for today
  const pageviews = rawData.length
  const uniqueVisitors = new Set(rawData.map(r => r.visitor_id)).size
  const uniqueSessions = new Set(rawData.map(r => r.session_id)).size
  const newVisitors = rawData.filter(r => r.is_new_visitor).length

  // Calculate bounced sessions
  const sessionPageCounts = rawData.reduce((acc, r) => {
    acc[r.session_id] = (acc[r.session_id] || 0) + 1
    return acc
  }, {} as Record<string, number>)
  const bouncedSessions = Object.values(sessionPageCounts).filter(count => count === 1).length

  // Calculate session durations
  const sessionsByTime: Record<string, number[]> = rawData.reduce((acc, r) => {
    if (!acc[r.session_id]) acc[r.session_id] = []
    acc[r.session_id].push(new Date(r.created_at).getTime())
    return acc
  }, {} as Record<string, number[]>)

  let totalDuration = 0
  let sessionsWithDuration = 0
  Object.values(sessionsByTime).forEach((times: number[]) => {
    if (times.length > 1) {
      const sorted = times.sort((a, b) => a - b)
      const duration = (sorted[sorted.length - 1] - sorted[0]) / 1000
      totalDuration += duration
      sessionsWithDuration++
    }
  })

  // Aggregate sources
  const sourceCount: Record<string, number> = {}
  rawData.forEach(r => {
    const source = r.referrer_source || 'direct'
    sourceCount[source] = (sourceCount[source] || 0) + 1
  })
  const sources = Object.entries(sourceCount)
    .map(([source, count]) => ({ source, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 10)

  // Aggregate pages
  const pageData: Record<string, { title: string | null; views: number }> = {}
  rawData.forEach(r => {
    const path = r.page_path || '/'
    if (!pageData[path]) {
      pageData[path] = { title: r.page_title, views: 0 }
    }
    pageData[path].views++
  })
  const top_pages = Object.entries(pageData)
    .map(([path, { title, views }]) => ({ path, title, views }))
    .sort((a, b) => b.views - a.views)
    .slice(0, 10)

  // Aggregate devices
  const deviceCount: Record<string, number> = {}
  rawData.forEach(r => {
    const device = r.device_type || 'unknown'
    deviceCount[device] = (deviceCount[device] || 0) + 1
  })

  // Aggregate browsers
  const browserCount: Record<string, number> = {}
  rawData.forEach(r => {
    const browser = r.browser || 'unknown'
    browserCount[browser] = (browserCount[browser] || 0) + 1
  })

  // Aggregate countries
  const countryData: Record<string, { count: number; code: string | null }> = {}
  rawData.forEach(r => {
    const country = r.country || 'Unknown'
    if (!countryData[country]) {
      countryData[country] = { count: 0, code: r.country_code || null }
    }
    countryData[country].count++
  })

  // Aggregate UTM sources from raw data
  const utmSourceCount: Record<string, number> = {}
  const utmMediumCount: Record<string, number> = {}
  const utmCampaignCount: Record<string, number> = {}
  rawData.forEach(r => {
    if (r.utm_source) {
      utmSourceCount[r.utm_source] = (utmSourceCount[r.utm_source] || 0) + 1
    }
    if (r.utm_medium) {
      utmMediumCount[r.utm_medium] = (utmMediumCount[r.utm_medium] || 0) + 1
    }
    if (r.utm_campaign) {
      utmCampaignCount[r.utm_campaign] = (utmCampaignCount[r.utm_campaign] || 0) + 1
    }
  })

  const utm_sources = Object.entries(utmSourceCount)
    .map(([source, count]) => ({ source, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 20)

  const utm_mediums = Object.entries(utmMediumCount)
    .map(([medium, count]) => ({ medium, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 20)

  const utm_campaigns = Object.entries(utmCampaignCount)
    .map(([campaign, count]) => ({ campaign, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 20)

  // Aggregate channels from raw data
  const channelCount: Record<string, number> = {}
  rawData.forEach(r => {
    const ch = (r as Record<string, unknown>).channel as string || 'direct'
    channelCount[ch] = (channelCount[ch] || 0) + 1
  })
  const channels = Object.entries(channelCount)
    .map(([channel, count]) => ({ channel, count }))
    .sort((a, b) => b.count - a.count)

  return {
    date: todayStr,
    pageviews,
    unique_visitors: uniqueVisitors,
    sessions: uniqueSessions,
    new_visitors: newVisitors,
    bounced_sessions: bouncedSessions,
    total_session_duration_seconds: Math.round(totalDuration),
    sessions_with_duration: sessionsWithDuration,
    sources,
    channels,
    top_pages,
    devices: deviceCount,
    browsers: browserCount,
    countries: countryData,
    utm_sources,
    utm_mediums,
    utm_campaigns
  }
}

// Decode URL-encoded search term
function decodeSearchTerm(term: string): string | null {
  try {
    const decoded = decodeURIComponent(term.replace(/\+/g, ' ')).trim()
    return decoded || null
  } catch {
    return term.trim() || null
  }
}

// Format duration in human-readable format (e.g., "2m 30s")
export function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds}s`
  const minutes = Math.floor(seconds / 60)
  const remainingSeconds = seconds % 60
  if (minutes < 60) {
    return remainingSeconds > 0 ? `${minutes}m ${remainingSeconds}s` : `${minutes}m`
  }
  const hours = Math.floor(minutes / 60)
  const remainingMinutes = minutes % 60
  return `${hours}h ${remainingMinutes}m`
}

// Format large numbers with K/M suffix
export function formatNumber(num: number): string {
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`
  return num.toString()
}
